import { createConnectTransport } from "@connectrpc/connect-web";
import type { Transport, Interceptor } from "@connectrpc/connect";
import { toBinary, toJsonString } from "@bufbuild/protobuf";
import { createErrorMappingTransport, TIMESTAMP_SKEW_CODE } from "./connect-error-mapping.js";
import {
    AuthenticationError,
    ConfigurationError,
    isAbortError,
    NetworkError,
    PolyesterError,
    TimestampSkewError,
} from "./errors.js";

export { isAbortError };

export type ConnectWireFormat = "binary" | "json";

export const POLYESTER_MOCK_HEADER = "x-polyester-mock";

export interface TransportConfig {
    apiUrl: string;
    interceptors?: Interceptor[];
    auth?: JwtAuthProvider | ApiKeyEd25519AuthProvider;
    /**
     * Connect wire format. Defaults to binary for production performance.
     * Use `json` for the API Playground visualization.
     */
    wireFormat?: ConnectWireFormat;
    /**
     * Custom fetch implementation for SDK HTTP requests. Defaults to the global
     * `fetch`, resolved on each request.
     */
    fetch?: typeof fetch;
}

export interface Transports {
    authApi: Transport;
    publicApi: Transport;
}

/** Connect transports available to services that call authenticated APIs. */
export type AuthApiTransports = Pick<Transports, "authApi">;

/** Connect transports available to services that call public APIs. */
export type PublicApiTransports = Pick<Transports, "publicApi">;

/** Connect transports available to services that call both public and authenticated APIs. */
export type AuthAndPublicApiTransports = Pick<Transports, "authApi" | "publicApi">;

/**
 * Generic JWT auth for HTTP/Connect endpoints that follow the Bearer token contract.
 */
export interface JwtAuthProvider {
    kind: "jwt";
    /**
     * Typically wraps the configured browser auth token storage. The SDK reads
     * the current value for every request and for every realtime auth check, so
     * keep this cheap and return the freshest credential rather than caching.
     */
    getToken: () => string | null | Promise<string | null>;
}

/**
 * Generic Ed25519 API key auth for HTTP/Connect endpoints that follow the
 * X-API-KEY-ID / X-API-TIMESTAMP / X-API-SIGNATURE contract.
 *
 * Each request is signed immediately before dispatch, but the runtime's fetch
 * may still queue it (connection limits). Bound concurrency for large bursts;
 * a request that waits past the backend's skew window fails with a retryable
 * `TimestampSkewError`, and retrying signs it again.
 */
export interface ApiKeyEd25519AuthProvider {
    kind: "api-key-ed25519";
    getKeyId: () => string | null | Promise<string | null>;
    /** Ed25519 secret key bytes (not hex) for signing. */
    getSecretKey: () => Uint8Array | null | Promise<Uint8Array | null>;
}

export interface ApiKeyEd25519SigningRequest {
    url: string | URL;
    method: string;
    body?: Uint8Array;
    timestamp?: string;
}

/**
 * Returns the fetch implementation used by SDK transports.
 */
export function makeFetch(fetchImpl?: typeof fetch): typeof fetch {
    const wrappedFetch = Object.assign(
        async (input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) => {
            // Normalize headers into a mutable Headers object
            const headers = new Headers(init?.headers);

            let res: Response | undefined;
            try {
                res = await (fetchImpl ?? fetch)(input, {
                    ...init,
                    headers,
                    redirect: "manual",
                });
            } catch (err) {
                if (isAbortError(err)) throw err;
                throw new NetworkError("Transport request failed", { cause: err });
            }
            // A custom or patched fetch (browser extensions, monitoring scripts) can
            // resolve without a Response.
            if (!res) throw new NetworkError("Transport request returned no response");
            // Connect discards non-Connect error bodies, so surface the problem+json
            // skew code here before it collapses into a bare HTTP status error.
            if (await isTimestampSkewProblem(res)) {
                throw new TimestampSkewError(
                    "API key timestamp is outside the allowed skew window.",
                );
            }
            return res;
        },
        fetch,
    );

    return wrappedFetch;
}

async function isTimestampSkewProblem(res: Response): Promise<boolean> {
    if (res.ok || !res.headers.get("content-type")?.includes("application/problem+json")) {
        return false;
    }
    try {
        const body: unknown = await res.clone().json();
        return (
            typeof body === "object" &&
            body !== null &&
            "code" in body &&
            body.code === TIMESTAMP_SKEW_CODE
        );
    } catch {
        return false;
    }
}

/**
 * Creates Connect transports for SDK service clients.
 */
export function createTransports(config: TransportConfig): Transports {
    const { apiUrl, interceptors = [], auth, wireFormat = "binary", fetch: fetchImpl } = config;
    const useBinaryFormat = wireFormat === "binary";
    const publicApi = createErrorMappingTransport(
        createConnectTransport({
            baseUrl: apiUrl,
            useBinaryFormat,
            interceptors,
            fetch: makeFetch(fetchImpl),
        }),
    );

    const authInterceptors = auth
        ? auth.kind === "jwt"
            ? [createAuthInterceptor(auth, { wireFormat }), ...interceptors]
            : [...interceptors, createAuthInterceptor(auth, { wireFormat })]
        : interceptors;
    const authApi = createErrorMappingTransport(
        createConnectTransport({
            baseUrl: apiUrl,
            useBinaryFormat,
            interceptors: authInterceptors,
            fetch: makeFetch(fetchImpl),
        }),
    );

    return { authApi, publicApi };
}

/**
 * Creates an interceptor that enables mock transport behavior.
 */
export function createMockInterceptor(getEnabled: () => boolean): Interceptor {
    return (next) => async (req) => {
        if (getEnabled()) req.header.set(POLYESTER_MOCK_HEADER, "true");
        return next(req);
    };
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
    if (typeof crypto === "undefined" || !crypto.subtle) {
        throw new ConfigurationError("SHA-256 not available in this environment");
    }
    const hash = await crypto.subtle.digest("SHA-256", bytes.slice());
    return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, "0")).join("");
}

let lastTimestamp = 0;
function nextTimestamp(): string {
    lastTimestamp = Math.max(lastTimestamp + 1, Date.now());
    return String(lastTimestamp);
}

function canonicalQueryString(params: URLSearchParams): string {
    const pairs: string[] = [];
    for (const [k, v] of params.entries()) {
        pairs.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
    }
    pairs.sort();
    return pairs.join("&");
}

/**
 * Creates API-key authentication headers for endpoints using the Polyester Ed25519 signing contract.
 */
export async function createApiKeyEd25519AuthHeaders(
    auth: ApiKeyEd25519AuthProvider,
    request: ApiKeyEd25519SigningRequest,
): Promise<Record<string, string>> {
    let keyId: string | null;
    let secretKey: Uint8Array | null;
    try {
        [keyId, secretKey] = await Promise.all([auth.getKeyId(), auth.getSecretKey()]);
    } catch (cause) {
        if (cause instanceof PolyesterError) throw cause;
        throw new ConfigurationError("API key credential provider failed", { cause });
    }
    if (!keyId || !secretKey) throw new ConfigurationError("Missing API key ID or secret key");
    if (!(secretKey instanceof Uint8Array) || secretKey.byteLength !== 32) {
        throw new ConfigurationError("API key secret key must contain exactly 32 bytes");
    }

    const urlObj = new URL(request.url);
    const timestamp = request.timestamp ?? nextTimestamp();
    const bodyHash = await sha256Hex(request.body ?? new Uint8Array(0));
    const canonicalQuery = canonicalQueryString(urlObj.searchParams);
    const canonical = `${timestamp}\n${request.method}\n${urlObj.pathname}\n${canonicalQuery}\n${bodyHash}`;
    const msgBytes = new TextEncoder().encode(canonical);
    // Loaded on demand: browser clients authenticate with JWTs and never sign.
    const { signAsync } = await import("@noble/ed25519");
    const sig = await signAsync(msgBytes, secretKey);
    const signatureHex = Array.from(sig, (b: number) => b.toString(16).padStart(2, "0")).join("");

    return {
        "X-API-KEY-ID": keyId,
        "X-API-TIMESTAMP": timestamp,
        "X-API-SIGNATURE": signatureHex,
    };
}

/**
 * Resolves a JWT credential and translates provider failures or invalid values
 * into stable SDK errors.
 */
export async function resolveJwtToken(auth: JwtAuthProvider): Promise<string | null> {
    let token: string | null;
    try {
        token = await auth.getToken();
    } catch (cause) {
        if (cause instanceof PolyesterError) throw cause;
        throw new AuthenticationError("JWT token provider failed", { cause });
    }
    if (token !== null && typeof token !== "string") {
        throw new ConfigurationError("JWT token provider must return a string or null");
    }
    return token;
}

/**
 * Creates an interceptor that attaches SDK authentication headers.
 */
export function createAuthInterceptor(
    auth: JwtAuthProvider | ApiKeyEd25519AuthProvider,
    options?: { wireFormat?: ConnectWireFormat },
): Interceptor {
    return (next) => async (req) => {
        const wireFormat = options?.wireFormat ?? "binary";
        if (auth.kind === "jwt") {
            const token = await resolveJwtToken(auth);
            if (token) {
                req.header.set("Authorization", `Bearer ${token}`);
            }
        } else if (auth.kind === "api-key-ed25519") {
            if (req.stream) {
                throw new ConfigurationError(
                    "API key authentication does not support streaming Connect RPCs",
                );
            }
            // Unary request: serialize the message using the method's input schema.
            const inputSchema = req.method.input;
            const bodyBytes =
                wireFormat === "json"
                    ? new TextEncoder().encode(toJsonString(inputSchema, req.message))
                    : toBinary(inputSchema, req.message);

            const headers = await createApiKeyEd25519AuthHeaders(auth, {
                url: req.url,
                method: req.requestMethod,
                body: bodyBytes,
            });
            for (const [key, value] of Object.entries(headers)) {
                req.header.set(key, value);
            }
        }
        return next(req);
    };
}
